import {isNil} from 'ramda';

import {downloadFile} from '@dms/shared';

import type {GeneratedReportDocument, ReportDocumentSource} from './ChartReportDocument';
import {embedDocumentFonts} from './embedDocumentFonts';

interface GenerateReportDocumentOptions {
  signal?: AbortSignal;
}

interface RequestPdfUrlInput {
  baseUrl: string;
  contentDispositionType: 'attachment' | 'inline';
  filename: string;
  html: string;
  signal: AbortSignal;
}

const PDF_REQUEST_TIMEOUT_MS = 30_000;
const BLOB_URL_REVOKE_DELAY_MS = 1_000;
const PDF_URL_REFRESH_BUFFER_MS = 30_000;
const MILLISECONDS_PER_SECOND = 1_000;

function getPdfUrl(value: unknown) {
  if (typeof value !== 'object' || isNil(value) || !('url' in value)) {
    return undefined;
  }
  if (typeof value.url !== 'string') {
    return undefined;
  }

  try {
    const url = new URL(value.url);
    return url.protocol === 'https:' ? value.url : undefined;
  } catch {
    return undefined;
  }
}

function createPrintResult(source: ReportDocumentSource): GeneratedReportDocument {
  return {
    kind: 'print',
    html: source.html,
    filename: source.filename.replace(/\.pdf$/i, '.html'),
  };
}

function getPdfFilename(filename: string) {
  return filename.replace(/\.pdf$/i, '');
}

function getPdfRefreshAfterMs(urls: string[]) {
  const expirationSeconds = urls
    .map((value) => Number(new URL(value).searchParams.get('X-Amz-Expires')))
    .filter((value) => Number.isFinite(value) && value > 0);
  if (expirationSeconds.length === 0) {
    return undefined;
  }

  const refreshAfterMs =
    Math.min(...expirationSeconds) * MILLISECONDS_PER_SECOND - PDF_URL_REFRESH_BUFFER_MS;
  return refreshAfterMs > 0 ? refreshAfterMs : undefined;
}

async function requestPdfUrl(input: RequestPdfUrlInput) {
  const response = await fetch(`${input.baseUrl}/html-to-pdf`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    signal: input.signal,
    body: JSON.stringify({
      content: input.html,
      contentDispositionType: input.contentDispositionType,
      filename: getPdfFilename(input.filename),
    }),
  });

  return response.ok ? getPdfUrl(await response.json()) : undefined;
}

export async function generateReportDocument(
  documentSource: ReportDocumentSource,
  options: GenerateReportDocumentOptions = {}
): Promise<GeneratedReportDocument> {
  const source = {...documentSource, html: await embedDocumentFonts(documentSource.html)};
  const baseUrl = process.env.OMNETIC_TOOLS_API_URL?.replace(/\/$/, '');

  if (!baseUrl) {
    return createPrintResult(source);
  }

  const controller = new AbortController();
  const handleExternalAbort = () => controller.abort();
  if (options.signal?.aborted) {
    controller.abort();
  } else {
    options.signal?.addEventListener('abort', handleExternalAbort, {once: true});
  }
  const timeoutId = window.setTimeout(() => controller.abort(), PDF_REQUEST_TIMEOUT_MS);

  try {
    const [openUrl, downloadUrl] = await Promise.all([
      requestPdfUrl({
        baseUrl,
        contentDispositionType: 'inline',
        filename: source.filename,
        html: source.html,
        signal: controller.signal,
      }),
      requestPdfUrl({
        baseUrl,
        contentDispositionType: 'attachment',
        filename: source.filename,
        html: source.html,
        signal: controller.signal,
      }),
    ]);
    if (isNil(openUrl) || isNil(downloadUrl)) {
      return createPrintResult(source);
    }

    const refreshAfterMs = getPdfRefreshAfterMs([openUrl, downloadUrl]);
    return {
      kind: 'pdf',
      filename: source.filename,
      openUrl,
      downloadUrl,
      ...(isNil(refreshAfterMs) ? {} : {refreshAfterMs}),
    };
  } catch (error) {
    controller.abort();
    if (options.signal?.aborted) {
      throw error;
    }
    return createPrintResult(source);
  } finally {
    window.clearTimeout(timeoutId);
    options.signal?.removeEventListener('abort', handleExternalAbort);
  }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  window.setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
  }, BLOB_URL_REVOKE_DELAY_MS);
}

function openPrintDocument(document: Extract<GeneratedReportDocument, {kind: 'print'}>) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    downloadBlob(new Blob([document.html], {type: 'text/html'}), document.filename);
    return;
  }

  const url = URL.createObjectURL(new Blob([document.html], {type: 'text/html'}));
  printWindow.opener = null;
  printWindow.addEventListener(
    'load',
    () => {
      printWindow.focus();
      printWindow.print();
      URL.revokeObjectURL(url);
    },
    {once: true}
  );
  printWindow.location.assign(url);
}

export function openReportDocument(document: GeneratedReportDocument) {
  if (document.kind === 'print') {
    openPrintDocument(document);
    return;
  }

  window.open(document.openUrl, '_blank', 'noopener,noreferrer');
}

export function downloadReportDocument(document: GeneratedReportDocument) {
  if (document.kind === 'print') {
    downloadBlob(new Blob([document.html], {type: 'text/html'}), document.filename);
    return;
  }

  downloadFile(document.downloadUrl, {fileName: document.filename});
}
